import LegalLayout, {
  LegalSection,
  InfoList,
  Columns,
  Subhead,
  Bullets,
  Note,
} from "@/components/ttt/LegalLayout"
import type { TttChrome } from "@/components/ttt/types"

interface Props {
  chrome: TttChrome
  lastUpdated: string
}

const mail = "turbotimetravel@gmail.com"

export default function Confidentiality({ chrome, lastUpdated }: Props) {
  return (
    <LegalLayout
      chrome={chrome}
      title="Politique de confidentialité"
      subtitle="Votre vie privée est importante pour nous. Cette politique explique comment TTT Corp collecte, utilise et protège vos données personnelles."
      lastUpdated={lastUpdated}
      cta={{
        title: "Questions sur vos données personnelles ?",
        subtitle: "Notre équipe respecte votre vie privée et répond à toutes vos questions.",
        secondaryHref: chrome.paths.cookies,
        secondaryLabel: "Politique des cookies",
      }}
    >
      <LegalSection title="1. Introduction">
        <p>
          TTT Corp, association loi 1901 située au 14 Rue Sanlecque, 44000 Nantes, s'engage à protéger la
          confidentialité des données personnelles de ses utilisateurs conformément au Règlement Général sur la
          Protection des Données (RGPD).
        </p>
        <p>
          Cette politique de confidentialité décrit comment nous collectons, utilisons, stockons et protégeons vos
          informations personnelles dans le cadre de nos activités d'édition et de diffusion culturelle.
        </p>
      </LegalSection>

      <LegalSection title="2. Données personnelles collectées">
        <Columns>
          <div>
            <Subhead>Données de contact et commande</Subhead>
            <Bullets
              items={[
                "Nom et prénom",
                "Adresse e-mail",
                "Adresse postale de livraison",
                "Informations de facturation",
              ]}
            />
          </div>
          <div>
            <Subhead>Données techniques</Subhead>
            <Bullets
              items={[
                "Adresse IP",
                "Type de navigateur et version",
                "Pages visitées et durée de visite",
                "Données de géolocalisation approximative",
              ]}
            />
          </div>
        </Columns>
        <Note title="Information importante">
          En tant qu'association à but non lucratif, nous ne collectons que les données strictement nécessaires à la
          gestion des commandes et à l'amélioration de nos services culturels.
        </Note>
      </LegalSection>

      <LegalSection title="3. Finalités du traitement">
        <p>Vos données personnelles sont traitées pour les finalités suivantes :</p>
        <Bullets
          items={[
            <>
              <span className="text-contrast">Gestion des commandes :</span> traitement et suivi de vos achats de
              livres
            </>,
            <>
              <span className="text-contrast">Livraison des produits :</span> expédition de vos commandes en Europe
            </>,
            <>
              <span className="text-contrast">Service client :</span> répondre à vos questions par email
            </>,
            <>
              <span className="text-contrast">Communication associative :</span> vous informer de nos actualités
              éditoriales (avec votre consentement)
            </>,
            <>
              <span className="text-contrast">Amélioration de nos services :</span> analyser l'utilisation de notre
              site pour l'optimiser
            </>,
            <>
              <span className="text-contrast">Obligations légales :</span> respecter nos obligations comptables
              associatives
            </>,
          ]}
        />
      </LegalSection>

      <LegalSection title="4. Base légale du traitement">
        <p>Le traitement de vos données repose sur :</p>
        <Bullets
          items={[
            <>
              <span className="text-contrast">Exécution d'un contrat :</span> pour le traitement et la livraison de
              vos commandes
            </>,
            <>
              <span className="text-contrast">Intérêt légitime :</span> pour l'amélioration de nos services culturels
              et la sécurité
            </>,
            <>
              <span className="text-contrast">Consentement :</span> pour la communication sur nos actualités
              éditoriales
            </>,
            <>
              <span className="text-contrast">Obligation légale :</span> pour le respect des obligations comptables
              associatives
            </>,
          ]}
        />
      </LegalSection>

      <LegalSection title="5. Durée de conservation des données">
        <Columns>
          <div>
            <Subhead>Données de commande</Subhead>
            <p className="text-content/80 text-sm">
              Conservées 10 ans pour les obligations comptables associatives.
            </p>
          </div>
          <div>
            <Subhead>Données marketing</Subhead>
            <p className="text-content/80 text-sm">Conservées 3 ans maximum ou jusqu'au désabonnement.</p>
          </div>
        </Columns>
        <Note title="Gestion responsable">
          Les données sont automatiquement supprimées à l'expiration des délais légaux ou sur demande de votre part.
        </Note>
      </LegalSection>

      <LegalSection title="6. Vos droits sur vos données">
        <p>
          Vous disposez de droits sur vos données personnelles que vous pouvez exercer à tout moment.
        </p>
        <Columns>
          <div>
            <Subhead>Droit d'accès et de rectification</Subhead>
            <Bullets
              items={[
                "Consulter vos données personnelles",
                "Corriger les informations inexactes",
                "Compléter les données incomplètes",
              ]}
            />
          </div>
          <div>
            <Subhead>Droit d'effacement et d'opposition</Subhead>
            <Bullets
              items={[
                "Supprimer vos données personnelles",
                "Vous opposer au traitement",
                "Limiter l'utilisation de vos données",
              ]}
            />
          </div>
        </Columns>
        <p>
          Pour exercer ces droits, contactez-nous à{" "}
          <a href={`mailto:${mail}`} className="text-primary hover:underline">
            {mail}
          </a>
          . Nous vous répondrons dans un délai de 30 jours maximum.
        </p>
      </LegalSection>

      <LegalSection title="7. Sécurité et protection des données">
        <p>
          TTT Corp met en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données
          personnelles.
        </p>
        <Columns>
          <div>
            <Subhead>Chiffrement</Subhead>
            <p className="text-content/80 text-sm">Connexions HTTPS et stockage sécurisé.</p>
          </div>
          <div>
            <Subhead>Accès restreint</Subhead>
            <p className="text-content/80 text-sm">Seuls les membres autorisés accèdent aux données.</p>
          </div>
          <div>
            <Subhead>Sauvegarde</Subhead>
            <p className="text-content/80 text-sm">Sauvegardes régulières et sécurisées.</p>
          </div>
        </Columns>
      </LegalSection>

      <LegalSection title="8. Partage et transfert des données">
        <p>
          TTT Corp ne vend, ne loue et ne partage vos données personnelles avec des tiers à des fins commerciales.
        </p>
        <Subhead>Partenaires techniques nécessaires</Subhead>
        <p className="text-content/80 text-sm">
          Vos données peuvent être partagées uniquement avec nos partenaires techniques indispensables :
        </p>
        <Bullets
          items={[
            <>
              <span className="text-contrast">Stripe :</span> pour le traitement sécurisé des paiements
            </>,
            <>
              <span className="text-contrast">Transporteurs :</span> pour la livraison de vos commandes en Europe
            </>,
            <>
              <span className="text-contrast">Hébergeur (Clever Cloud) :</span> pour le stockage sécurisé des
              données
            </>,
          ]}
        />
        <Note title="Engagement associatif">
          En tant qu'association à but non lucratif, nous nous engageons à ne jamais utiliser vos données à des fins
          commerciales ou les partager avec des entreprises tierces.
        </Note>
      </LegalSection>

      <LegalSection title="9. Cookies et technologies similaires">
        <p>Nous utilisons des cookies de manière limitée et respectueuse de votre vie privée.</p>
        <Columns>
          <div>
            <Subhead>Cookies essentiels</Subhead>
            <Bullets
              items={[
                "Fonctionnement du panier d'achat",
                "Maintien de votre session",
                "Sécurité du site (protection CSRF)",
                "Préférences de navigation",
              ]}
            />
          </div>
          <div>
            <Subhead>Ce que nous n'utilisons PAS</Subhead>
            <Bullets
              items={[
                "Cookies publicitaires",
                "Tracking de sites tiers",
                "Profilage comportemental",
                "Vente de données à des tiers",
              ]}
            />
          </div>
        </Columns>
        <Note title="Plus d'informations">
          Pour en savoir plus sur notre utilisation des cookies,{" "}
          <a href={chrome.paths.cookies} className="text-primary hover:underline">
            consultez notre politique des cookies
          </a>
          .
        </Note>
      </LegalSection>

      <LegalSection title="10. Contact et réclamations">
        <Columns>
          <div>
            <Subhead>Responsable du traitement</Subhead>
            <InfoList
              items={[
                { label: "Dénomination", value: "TTT Corp — Association loi 1901" },
                {
                  label: "Adresse",
                  value: (
                    <>
                      14 Rue Sanlecque
                      <br />
                      44000 Nantes
                    </>
                  ),
                },
                {
                  label: "E-mail",
                  value: (
                    <a href={`mailto:${mail}`} className="text-primary hover:underline">
                      {mail}
                    </a>
                  ),
                },
              ]}
            />
          </div>
          <div>
            <Subhead>Autorité de contrôle</Subhead>
            <p className="text-content/80 text-sm">
              Si vous estimez que vos droits ne sont pas respectés, vous pouvez adresser une réclamation à la CNIL.
            </p>
            <p className="text-content/80 text-sm">
              <a
                href="https://www.cnil.fr"
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                www.cnil.fr
              </a>
            </p>
          </div>
        </Columns>
      </LegalSection>
    </LegalLayout>
  )
}
